import React, { useContext, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FilterContext } from "../../context/Filter";

const columns = [
  { header: "Dealer", accessorKey: "Dealer" },
  { header: " C3 Month-Year", accessorKey: "C3_MONTH_YEAR" },
  { header: "LOB", accessorKey: "LOB" },
  { header: "PPL", accessorKey: "PPL" },
  { header: "PL", accessorKey: "PL" },
  { header: "Model", accessorKey: "MODEL" },
  { header: "Financier", accessorKey: "FINANCIER" },
  { header: "Application", accessorKey: "APPLICATION" },
  { header: "C3 Date", accessorKey: "TML_INVOICE_DT" },
  { header: "Qty", accessorKey: "doc_count" },
  // {
  //   header: "Quantity",
  //   accessorFn: (row) => row.Quantity,
  // },
];

export default function DealerTable({ data }) {
  const { open } = useContext(FilterContext);

  // Memoize the safe data processing to prevent unnecessary re-renders
  const flatData = useMemo(() => {
    // Early return if data is not valid
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    try {
      return data.map((item, index) => {
        // Handle potential null/undefined items
        if (!item || typeof item !== 'object') {
          console.warn(`Invalid item at index ${index}:`, item);
          return {
            doc_count: 0,
            Quantity: 0,
            id: `invalid-${index}`,
          };
        }

        // Safely extract key object
        const keyObj = item.key && typeof item.key === "object" && item.key !== null
          ? item.key
          : {};

        // Return flattened object with safe defaults
        return {
          ...keyObj,
          doc_count: item.doc_count ?? 0,
          Quantity: item.Quantity?.value ?? item.Quantity ?? 0,
          // Add a unique identifier for React key purposes
          id: item.id || `item-${index}`,
        };
      });
    } catch (error) {
      console.error("Error processing table data:", error);
      return [];
    }
  }, [data]);

  // Memoize the table instance to prevent recreation on every render
  const table = useReactTable({
    data: flatData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Add a stable key for the table
    getRowId: (row, index) => row.id || `row-${index}`,
  });

  // Early return if no data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="overflow-x-auto">
        <div className={`max-h-80 ${open ? "w-[1020px]" : "w-[1180px]"} overflow-y-auto border border-gray-300 rounded`}>
          <div className="min-w-full overflow-x-auto">
            <table className={`${open ? "w-[950px]" : "w-[1200px]"}`}>
              <thead className="bg-[#2374BF] text-sm text-white sticky top-0 z-10">
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={`header-${index}`}
                      className="border border-gray-300 px-4 py-2 text-center bg-[#2374BF] whitespace-nowrap"
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center py-4 text-gray-500 border border-gray-300"
                  >
                    No data available.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div
        className={`max-h-80 ${open ? "w-[1020px]" : "w-[1180px]"
          } overflow-y-auto border border-gray-300 rounded`}
      >
        <div className="min-w-full overflow-x-auto">
          <table className={`${open ? "w-[950px]" : "w-[1200px]"}`}>
            <thead className="bg-[#2374BF] text-sm text-white sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="border border-gray-300 px-4 py-2 text-center bg-[#2374BF] whitespace-nowrap"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center py-4 text-gray-500 border border-gray-300"
                  >
                    No data available.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={idx % 2 === 1 ? "bg-[#DBDBDB]" : "bg-white"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="border border-gray-300 px-3 py-2 text-center text-xs whitespace-nowrap"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}